import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  HomeIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  ShieldCheckIcon,
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import ThemeToggle from '../common/ThemeToggle'
import { dropdown as dropdownVariants } from '../../config/animations'

const Navbar = () => {
  const location = useLocation()
  const { user, isAuthenticated, isAdmin, logout } = useAuth()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const dropdownRef = useRef(null)

  const isActive = (path) => location.pathname === path

  const navItems = [
    { name: 'Home', path: '/', icon: HomeIcon },
    { name: 'Events', path: '/events', icon: CalendarIcon },
    { name: 'Cities', path: '/cities', icon: BuildingOfficeIcon },
  ]

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false)
    setIsDropdownOpen(false)
  }, [location.pathname])

  return (
    <nav className="bg-bg-elevated border-b border-border sticky top-0 z-sticky
                    backdrop-blur-sm bg-opacity-95 dark:bg-opacity-95
                    transition-colors duration-200">
      <div className="app-container">
        <div className="flex justify-between h-16">
          {/* Logo + Desktop nav */}
          <div className="flex items-center gap-1">
            <Link
              to="/"
              className="flex items-center gap-2 hover:opacity-90 transition-opacity duration-150
                         flex-shrink-0"
            >
              <img
                src="/images/logo-icon.png"
                alt=""
                className="h-8 sm:h-9 w-auto"
              />
              <span className="text-xl sm:text-2xl font-bold text-primary-600">
                Garage Sale Mapper
              </span>
            </Link>

            {/* Desktop nav links */}
            <div className="hidden sm:flex items-center ml-6 gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`inline-flex items-center px-3 py-2 text-sm font-medium
                             rounded-lg transition-colors duration-150 touch-target
                             ${isActive(item.path)
                               ? 'text-primary-700 bg-primary-50 dark:text-primary-600 dark:bg-primary-50'
                               : 'link-nav hover:bg-surface-100'
                             }`}
                >
                  <item.icon className="h-5 w-5 mr-1.5" />
                  {item.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Right side: theme toggle + user menu + mobile hamburger */}
          <div className="flex items-center gap-1">
            <ThemeToggle />

            {/* User menu (desktop) */}
            <div className="hidden sm:block relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="btn-icon rounded-full"
                aria-expanded={isDropdownOpen}
                aria-haspopup="true"
              >
                <div className="relative">
                  <UserCircleIcon className="h-7 w-7" />
                  {isAdmin && (
                    <div className="absolute -top-1 -right-1 h-3 w-3 bg-info-500 rounded-full flex items-center justify-center">
                      <ShieldCheckIcon className="h-2 w-2 text-white" />
                    </div>
                  )}
                </div>
              </button>

              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    {...dropdownVariants}
                    className="absolute right-0 mt-2 w-56 rounded-xl shadow-lg
                               bg-bg-elevated border border-border
                               z-dropdown overflow-hidden"
                  >
                    <div className="py-1" role="menu" aria-orientation="vertical">
                      {isAuthenticated ? (
                        <>
                          <div className="px-4 py-3 border-b border-border">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-text">
                                  {user?.name || user?.email}
                                </p>
                                <p className="text-xs text-text-muted mt-0.5">Signed in</p>
                              </div>
                              {isAdmin && (
                                <span className="badge-info">
                                  <ShieldCheckIcon className="h-3 w-3" />
                                  Admin
                                </span>
                              )}
                            </div>
                          </div>
                          <Link
                            to="/account"
                            onClick={() => setIsDropdownOpen(false)}
                            className="flex items-center px-4 py-2.5 text-sm text-text-secondary
                                       hover:bg-surface-100 hover:text-text transition-colors"
                            role="menuitem"
                          >
                            <Cog6ToothIcon className="h-5 w-5 mr-2.5 text-text-muted" />
                            Account Settings
                          </Link>
                          {isAdmin && (
                            <>
                              <div className="divider my-1" />
                              <a
                                href="/avo"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setIsDropdownOpen(false)}
                                className="flex items-center px-4 py-2.5 text-sm text-info-600
                                           hover:bg-info-50 transition-colors"
                                role="menuitem"
                              >
                                <ShieldCheckIcon className="h-5 w-5 mr-2.5 text-info-400" />
                                Admin Panel
                              </a>
                              <a
                                href="/sidekiq"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setIsDropdownOpen(false)}
                                className="flex items-center px-4 py-2.5 text-sm text-info-600
                                           hover:bg-info-50 transition-colors"
                                role="menuitem"
                              >
                                <Cog6ToothIcon className="h-5 w-5 mr-2.5 text-info-400" />
                                Background Jobs
                              </a>
                            </>
                          )}
                          <div className="divider my-1" />
                          <button
                            onClick={() => {
                              logout()
                              setIsDropdownOpen(false)
                            }}
                            className="flex items-center w-full px-4 py-2.5 text-sm text-text-secondary
                                       hover:bg-surface-100 hover:text-text transition-colors"
                            role="menuitem"
                          >
                            <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2.5 text-text-muted" />
                            Sign out
                          </button>
                        </>
                      ) : (
                        <>
                          <Link
                            to="/login"
                            onClick={() => setIsDropdownOpen(false)}
                            className="block px-4 py-2.5 text-sm text-text-secondary
                                       hover:bg-surface-100 hover:text-text transition-colors"
                            role="menuitem"
                          >
                            Sign in
                          </Link>
                          <Link
                            to="/register"
                            onClick={() => setIsDropdownOpen(false)}
                            className="block px-4 py-2.5 text-sm text-text-secondary
                                       hover:bg-surface-100 hover:text-text transition-colors"
                            role="menuitem"
                          >
                            Sign up
                          </Link>
                        </>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="sm:hidden btn-icon"
              aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="sm:hidden border-t border-border overflow-hidden"
          >
            <div className="px-4 py-3 space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center px-3 py-3 text-base font-medium
                             rounded-lg transition-colors duration-150
                             ${isActive(item.path)
                               ? 'text-primary-700 bg-primary-50 dark:text-primary-600 dark:bg-primary-50'
                               : 'text-text-secondary hover:text-text hover:bg-surface-100'
                             }`}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              ))}

              <div className="divider my-2" />

              {isAuthenticated ? (
                <>
                  <div className="px-3 py-2 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-text">
                        {user?.name || user?.email}
                      </p>
                      <p className="text-xs text-text-muted">Signed in</p>
                    </div>
                    {isAdmin && (
                      <span className="badge-info">
                        <ShieldCheckIcon className="h-3 w-3" />
                        Admin
                      </span>
                    )}
                  </div>
                  <Link
                    to="/account"
                    className="flex items-center px-3 py-3 text-base text-text-secondary
                               hover:text-text hover:bg-surface-100 rounded-lg transition-colors"
                  >
                    <Cog6ToothIcon className="h-5 w-5 mr-3 text-text-muted" />
                    Account Settings
                  </Link>
                  {isAdmin && (
                    <>
                      <div className="divider my-1" />
                      <a
                        href="/avo"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center px-3 py-3 text-base text-info-600
                                   hover:bg-info-50 rounded-lg transition-colors"
                      >
                        <ShieldCheckIcon className="h-5 w-5 mr-3 text-info-400" />
                        Admin Panel
                      </a>
                      <a
                        href="/sidekiq"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center px-3 py-3 text-base text-info-600
                                   hover:bg-info-50 rounded-lg transition-colors"
                      >
                        <Cog6ToothIcon className="h-5 w-5 mr-3 text-info-400" />
                        Background Jobs
                      </a>
                    </>
                  )}
                  <div className="divider my-1" />
                  <button
                    onClick={logout}
                    className="flex items-center w-full px-3 py-3 text-base text-text-secondary
                               hover:text-text hover:bg-surface-100 rounded-lg transition-colors"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3 text-text-muted" />
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="flex items-center px-3 py-3 text-base text-text-secondary
                               hover:text-text hover:bg-surface-100 rounded-lg transition-colors"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/register"
                    className="flex items-center px-3 py-3 text-base font-medium
                               text-primary-600 hover:text-primary-700
                               hover:bg-primary-50 rounded-lg transition-colors"
                  >
                    Sign up
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}

export default Navbar
